/**
 * Drives the Trustpilot review carousel embedded in the "trustpilot-reviews" page content
 * (rendered via the product page's "REVIEWS" collapsible tab). This used to live as an inline
 * <script> inside the page content itself, relying on document.currentScript.closest
 * ('[data-tp-carousel]') to find its own container — confirmed broken in production: Shopify's
 * page editor strips that custom data-* attribute on save (the live element only ever has
 * class="trustpilot-wrapper"), so the lookup always returned null and the script silently did
 * nothing. This version keys off the .trustpilot-wrapper class instead, which does survive, and
 * lives here as a real theme asset instead of page-content guesswork.
 */
(function () {
  function setupCarousel(root) {
    const track = root.querySelector('.trustpilot-cards');
    const prev = root.querySelector('.tp-nav--prev');
    const next = root.querySelector('.tp-nav--next');
    if (!track || !prev || !next) return;

    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

    const getStep = () => {
      const card = track.querySelector('.trustpilot-card');
      if (!card) return 0;
      const cardW = card.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(track).gap || '0');
      return cardW + gap;
    };

    const cardsPerPage = () => (isMobile() ? 1 : 2);

    const updateButtons = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const x = track.scrollLeft;

      const hasOverflow = track.scrollWidth > track.clientWidth + 2;
      prev.style.display = hasOverflow ? '' : 'none';
      next.style.display = hasOverflow ? '' : 'none';

      prev.disabled = x <= 2;
      next.disabled = x >= maxScroll - 2;
    };

    const scrollPage = (dir) => {
      const step = getStep();
      const per = cardsPerPage();
      // No explicit `behavior` here on purpose — the track's CSS already sets
      // scroll-behavior: smooth, and letting that govern the animation (rather than also
      // passing behavior: 'smooth' here) is the more robust way to combine JS-triggered
      // scrolling with scroll-snap-type: mandatory.
      track.scrollBy({ left: dir * step * per });
    };

    prev.addEventListener('click', () => scrollPage(-1));
    next.addEventListener('click', () => scrollPage(1));
    track.addEventListener('scroll', () => requestAnimationFrame(updateButtons));
    window.addEventListener('resize', () => requestAnimationFrame(updateButtons));

    updateButtons();
  }

  function init() {
    document.querySelectorAll('.trustpilot-wrapper').forEach(function (root) {
      if (root.dataset.tpCarouselInit) return;
      root.dataset.tpCarouselInit = 'true';
      setupCarousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The REVIEWS tab is a native <details> element — its content is already present in the DOM
  // from page load (just visually collapsed), so init() above should catch it. But in case the
  // accordion is ever changed to inject content on first expand, also re-run whenever any
  // <details> on the page is opened.
  document.addEventListener('toggle', function (event) {
    if (event.target && event.target.tagName === 'DETAILS' && event.target.open) {
      init();
    }
  }, true);
})();
